import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { CatalogApi } from '../api';

const CartContext = createContext(null);
const KEY = 'gt_cart';

// Cart line key = product + pack size (same product, different weight = own line).
const keyOf = (productId, weight) => `${productId}__${weight}`;

export function CartProvider({ children }) {
  const [lines, setLines] = useState({});

  useEffect(() => {
    try {
      setLines(JSON.parse(localStorage.getItem(KEY) || '{}'));
    } catch (_) { /* ignore */ }
  }, []);

  const save = useCallback((next) => {
    setLines(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  }, []);

  const add = useCallback((product, variant, qty = 1) => {
    const weight = variant?.weight || '1kg';
    const price = variant?.price ?? product.price;
    const mrp = variant?.mrp ?? product.mrp ?? price;
    const k = keyOf(product._id, weight);

    setLines((prev) => {
      const existing = prev[k];
      const next = {
        ...prev,
        [k]: existing
          ? { ...existing, qty: existing.qty + qty }
          : {
              productId: product._id,
              slug: product.slug,
              title: product.title,
              brand: product.brand,
              image: product.images?.[0] || '',
              weight,
              price,
              mrp,
              moq: variant?.moq || 0,
              moqPrice: variant?.moqPrice || 0,
              qty,
            },
      };
      localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setQty = useCallback((productId, weight, qty) => {
    setLines((prev) => {
      const k = keyOf(productId, weight);
      const next = { ...prev };
      if (qty <= 0) delete next[k];
      else next[k] = { ...next[k], qty };
      localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const remove = useCallback((productId, weight) => setQty(productId, weight, 0), [setQty]);

  const clear = useCallback(() => save({}), [save]);

  // Re-price every line for a delivery pincode. State/district pricing lives on
  // the server, so we re-fetch each product and pull the matching pack's price.
  // `loc` carries the delivery address's state/district — pricing resolves
  // PIN → district → state, and the server can't derive a district from a PIN.
  const repriceForPincode = useCallback(async (pincode, loc = {}) => {
    if (!pincode) return;
    const current = Object.values(lines);
    if (!current.length) return;

    const params = {
      pincode,
      ...(loc.state ? { state: loc.state } : {}),
      ...(loc.district ? { district: loc.district } : {}),
    };

    const ids = [...new Set(current.map((l) => l.productId))];
    const map = {};
    await Promise.all(
      ids.map((id) =>
        CatalogApi.product(id, params)
          .then((d) => { map[id] = d.product; })
          .catch(() => {})
      )
    );

    setLines((prev) => {
      const next = { ...prev };
      Object.entries(next).forEach(([k, l]) => {
        const p = map[l.productId];
        if (!p) return;
        const v = (p.variants || []).find((x) => x.weight === l.weight);
        if (!v) return;
        next[k] = {
          ...l,
          price: v.price,
          mrp: v.mrp ?? v.price,
          moq: v.moq || 0,
          moqPrice: v.moqPrice || 0,
        };
      });
      localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    });
  }, [lines]);

  const items = useMemo(
    () =>
      Object.values(lines).map((l) => {
        // Bulk (MOQ) price kicks in once the quantity reaches the threshold.
        const bulk = l.moq > 0 && l.moqPrice > 0 && l.qty >= l.moq;
        return { ...l, unit: bulk ? l.moqPrice : l.price, bulkApplied: bulk };
      }),
    [lines]
  );

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.unit * i.qty, 0), [items]);
  const grossMrp = useMemo(() => items.reduce((s, i) => s + (i.mrp || i.unit) * i.qty, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, count, subtotal, grossMrp, add, setQty, remove, clear, repriceForPincode }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
